import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./TableBody.module.scss";
import { MouseEvent, useState } from "react";
import { CheckIcon, DotsThreeIcon, DownloadIcon, RenameIcon, TrashIcon } from "@/frontend/shared/assets/icons";
import { NormalButton, SquareButton } from "@/frontend/shared/ui/Button";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { getFileUrl } from "../../model/services/getFileUrl";
import { deleteFile } from "../../model/services/deleteFile";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { RenameModal } from "@/frontend/components/RenameModal/RenameModal";
import { patchRenameFile } from "../../model/services/patchRenameFile";
import { formatDateMonthYear } from "@/frontend/shared/lib/formatDateMonthYear/formatDateMonthYear";
import { UnlockIcon } from "@/frontend/shared/assets/icons/UnlockIcon";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { AxiosError } from "axios";
import clsx from "clsx";
import { UsersAvatar } from "@/frontend/shared/ui/UsersAvatar/UsersAvatar";
import { getFileExtension } from "@/frontend/shared/lib/getFileExtension/getFileExtension";
import { EAccessDataStatus, EInvestorAccess } from "@/frontend/features/Company/model/types/types";
import { postAccessControlsRequest } from "@/frontend/features/Company/model/postAccessControlsRequest";
import { FileType } from "@/frontend/shared/ui/FileType/FileType";

export type TDataRoomRow = {
  filename: string;
  id: number;
  createdAt: string;
  createdBy: {
    firstName: string;
    lastName: string;
    profileImageUrl: string;
    organization: {
      name: string;
      id: number;
    };
  };
  parsedData: {
    title: string;
    description: string;
  };
};

export function TableBody({
  data,
  triggerRefetch,
  companyId,
  dataStatusFromAccess,
  companyDataId,
  triggerRefetchAccess,
  isLoadingRequestAccess,
  setIsLoadingRequestAccess,
  isDataRoomPending
}: {
  data: TDataRoomRow[];
  triggerRefetch: () => void;
  companyId: number;
  dataStatusFromAccess?: EAccessDataStatus;
  companyDataId?: number;
  triggerRefetchAccess: () => void;
  isLoadingRequestAccess: boolean;
  setIsLoadingRequestAccess: (value: boolean) => void;
  isDataRoomPending?: boolean;
}) {
  const [shownDropshadow, setShownDropshadow] = useState(-1);
  const [isOpenDeleteFileContainer, setIsOpenDeleteFileContainer] = useState({ id: -1, isOpen: false });
  const [isOpenRenameFileContainer, setIsOpenRenameFileContainer] = useState({ id: -1, isOpen: false });
  const [renameModalValue, setRenameModalValue] = useState("");

  async function onDeleteFile() {
    setIsOpenDeleteFileContainer({ id: -1, isOpen: false });
    try {
      await deleteFile({ id: companyId, fileId: isOpenDeleteFileContainer.id });
      triggerRefetch();
      snackbarConstructor("File successfully deleted", "normal");
    } catch (error) {
      snackbarConstructor("Error deleting file", "error");
      throw new Error();
    }
  }

  async function onRenameFile() {
    setIsOpenRenameFileContainer({ id: -1, isOpen: false });

    try {
      await patchRenameFile({ fileId: isOpenRenameFileContainer.id, id: companyId, title: renameModalValue });
      triggerRefetch();
      snackbarConstructor("File successfully renamed", "normal");
    } catch (error) {
      snackbarConstructor("Error renaming file", "error");
      throw new Error();
    } finally {
      setRenameModalValue("");
    }
  }

  async function downloadAndNavigate(fileId: number, filename: string) {
    try {
      const newTab = window.open("", "_blank"); // Open tab immediately

      if (!newTab) {
        console.error("Pop-up blocked or failed to open.");
        return;
      }

      const url = await getFileUrl({ companyId, fileId });
      if (!url) return;

      newTab.location.href = url; // Set the URL after fetching it

    } catch (error) {
      console.error("Error opening file:", error);
    }
  }

  function onCloseModal() {
    setIsOpenRenameFileContainer({ id: -1, isOpen: false });
    setRenameModalValue("");
  }

  function onOpenModal(e: MouseEvent<HTMLDivElement>, item: TDataRoomRow) {
    e.stopPropagation();
    setIsOpenDeleteFileContainer({ id: item.id, isOpen: true });
  }

  function onOpenRenameModal(e: MouseEvent<HTMLDivElement>, item: TDataRoomRow) {
    setRenameModalValue(item?.parsedData.title || item?.filename || "");
    e.stopPropagation();
    setIsOpenRenameFileContainer({ id: item.id, isOpen: true });
  }

  function shownButtonStatus() {
    switch (true) {
      case dataStatusFromAccess === EAccessDataStatus.PENDING_REQUEST || isDataRoomPending:
        return (
          <NormalButton size="medium" variant="secondary" className={styles.statusButtonSent}>
            <CheckIcon className={styles.unlockIcon} />
            Access Request Sent
          </NormalButton>
        );
      case dataStatusFromAccess === EAccessDataStatus.NO_ACCESS ||
        dataStatusFromAccess === EAccessDataStatus.PROFILE_DATA:
        return (
          <NormalButton
            size="medium"
            variant="primary"
            className={styles.statusButton}
            onClick={onConfirmRequestDataAccess}
            isSubmitting={isLoadingRequestAccess}
          >
            <UnlockIcon className={styles.unlockIcon} />
            <Typography variant="label-4" color="greyscale100">
              Request Data Access
            </Typography>
          </NormalButton>
        );
      default:
        return null;
    }
  }

  const bluredContainer = (
    <div className={styles.bluredContainer}>
      <div key={0} className={clsx(styles.row, styles.bluredRow)}>
        <div className={styles.labelContainer}>
          <div className={styles.fileFormatContainer}>
            <p className={styles.fileFormat}>PDF</p>
          </div>
          <div className={styles.fileContainer}>
            <Typography variant="label-4" className={styles.fileInfoText}>
              Annual Report
            </Typography>
            <div className={styles.fileInfoContainer}>
              <Typography variant="label-4" color="greyscale500" className={styles.fileInfoText}>
                report2024.pdf
              </Typography>
            </div>
          </div>
        </div>
        <div className={styles.labelContainer}>
          <img src={"https://example.com/images/johndoe.jpg"} alt="user" className={styles.userImg} />
          <div className={styles.userContainer}>
            <Typography variant="label-4">John Doe</Typography>
            <Typography variant="body-3" color="greyscale500">
              Acme Corp
            </Typography>
          </div>
        </div>
        <div className={styles.labelContainer}>
          <Typography variant="body-2">August 15, 2024</Typography>
        </div>
        <div className={styles.labelContainer}>
          <Typography variant="body-2">Detailed annual financial report.</Typography>
        </div>
        <div className={styles.actionsLabelContainer}>
          <SquareButton
            size="small"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              setShownDropshadow(0);
            }}
          >
            <DotsThreeIcon className={styles.threeDotsIcon} />
          </SquareButton>
        </div>
      </div>
      <div className={styles.blur}>
        <div className={styles.blurWrapper}>{shownButtonStatus()}</div>
      </div>
    </div>
  );

  async function onConfirmRequestDataAccess() {
    setIsLoadingRequestAccess(true);
    try {
      await postAccessControlsRequest({ companyId: companyDataId || 0, access: EInvestorAccess.FULL });
      triggerRefetchAccess();
      snackbarConstructor("Access request sent", "normal");
    } catch (error) {
      snackbarConstructor("Request data access error", "error");
      reportErrorToSentry(error as AxiosError, "DATAROOM: /access-controls/request");
      setIsLoadingRequestAccess(false);
    }
  }

  const isBluredContainerShown =
    dataStatusFromAccess === EAccessDataStatus.NO_ACCESS ||
    dataStatusFromAccess === EAccessDataStatus.PENDING_REQUEST ||
    dataStatusFromAccess === EAccessDataStatus.PROFILE_DATA;

  return (
    <div className={styles.container}>
      {isBluredContainerShown && bluredContainer}
      {data?.map((item, index) => (
        <div key={index} className={styles.row}>
          <div className={styles.labelContainer}>
            <FileType type={getFileExtension(item.filename).toUpperCase()} />
            <div className={styles.fileContainer}>
              <Typography variant="label-4" className={styles.fileInfoText} color="greyscale900">
                {item.parsedData.title || "File"}
              </Typography>
              <div className={styles.fileInfoContainer}>
                <Typography variant="body-3" color="greyscale600" className={styles.fileInfoText}>
                  {item.filename}
                </Typography>
              </div>
            </div>
          </div>
          <div className={styles.labelContainer}>
            <div className={styles.userWrapper}>
              <UsersAvatar
                nameInitials={item.createdBy.firstName[0] + item.createdBy.lastName[0]}
                organizationId={item.createdBy.organization.id}
                profileImg={item.createdBy.profileImageUrl}
              />
              <div className={styles.userContainer}>
                <Typography variant="label-4" color="greyscale900">
                  {item.createdBy.firstName} {item.createdBy.lastName}
                </Typography>
                <Typography variant="body-3" color="greyscale600">
                  {item?.createdBy?.organization?.name || "-"}
                </Typography>
              </div>
            </div>
          </div>
          <div className={clsx(styles.labelContainer, styles.createdAt)}>
            <Typography variant="body-2" color="greyscale600">
              {formatDateMonthYear(item.createdAt)}
            </Typography>
          </div>
          <div className={clsx(styles.labelContainer, styles.summary)}>
            <Typography variant="body-2" color="greyscale900">
              {item.parsedData.description || "-"}
            </Typography>
          </div>

          <div className={styles.actionsLabelContainer}>
            <SquareButton
              size="small"
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                setShownDropshadow(index);
              }}
              className={styles.contextButton}
            >
              <DotsThreeIcon className={styles.threeDotsIcon} />
            </SquareButton>
            <DropshadowMenu
              isOpen={shownDropshadow === index}
              onChange={() => setShownDropshadow(index)}
              onClose={() => setShownDropshadow(-1)}
              className={styles.optionsContainerClassName}
              options={[
                <div className={styles.dropshadowOption} onClick={() => downloadAndNavigate(item.id, item.filename)}>
                  <DownloadIcon />
                  <Typography variant="label-4" color="greyscale900">
                    Download
                  </Typography>
                </div>,
                <div className={styles.dropshadowOption} onClick={(e) => onOpenRenameModal(e, item)}>
                  <RenameIcon />
                  <Typography variant="label-4" color="greyscale900">
                    Rename
                  </Typography>
                </div>,

                <div
                  className={clsx(styles.dropshadowOption, styles.deleteOption)}
                  onClick={(e) => onOpenModal(e, item)}
                >
                  <TrashIcon />
                  <Typography variant="label-4" color="greyscale600">
                    Delete
                  </Typography>
                </div>
              ]}
              top="-10px"
              right="0px"
              dividerIndexRate={2}
              minWidth="0px"
            />
          </div>
        </div>
      ))}
      <ConfirmationModal
        isOpen={isOpenDeleteFileContainer.isOpen}
        onClose={() => setIsOpenDeleteFileContainer({ id: -1, isOpen: false })}
        onConfirm={() => onDeleteFile()}
        title="Delete file?"
        content="This file will be deleted permanently. Are you sure you want to delete it?"
        confirmButtonText="Delete"
        dangerConfirm
      />
      <RenameModal
        isOpen={isOpenRenameFileContainer.isOpen}
        onClose={onCloseModal}
        onSuccess={onRenameFile}
        value={renameModalValue}
        setValue={setRenameModalValue}
        title="Rename"
        placeholder="Enter new filename"
      />
    </div>
  );
}
